import React from 'react'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { usePageTitle } from '../../components/common/PageTitleContext'
import { getPricing, type PricingPrice, type PricingResponse, type PricingType } from '../../api/pricing'
import {
  getSuperadminSubscriptionSettings,
  saveSuperadminSubscriptionSettings,
  type ProductDefinition,
} from '../../api/superadminSubscriptions'

type NoticeState = {
  type: 'success' | 'error' | 'info'
  message: string
}

type PricingDebugEntry = {
  id: string
  requestedAt: string
  request: {
    type: PricingType
    currency?: string
    force?: boolean
    path: string
  }
  status: 'success' | 'error'
  response?: PricingResponse
  error?: {
    message: string
    status?: number
    data?: unknown
  }
}

const fallbackGroups: ProductDefinition['group'][] = ['individual', 'station', 'credits', 'seat_addon']
const currencyFlags: Record<string, string> = {
  EUR: '🇪🇺',
  GBP: '🇬🇧',
  USD: '🇺🇸',
}

const ProductManagerPage: React.FC = () => {
  usePageTitle('Products', { showBreadcrumbs: false })

  const [products, setProducts] = React.useState<ProductDefinition[]>([])
  const [productGroups, setProductGroups] = React.useState<string[]>([])
  const [pricingCacheTtlHours, setPricingCacheTtlHours] = React.useState(6)
  const [pricingLastUpdated, setPricingLastUpdated] = React.useState<string | null>(null)
  const [loading, setLoading] = React.useState(false)
  const [saving, setSaving] = React.useState(false)
  const [notice, setNotice] = React.useState<NoticeState | null>(null)
  const [selectedIndex, setSelectedIndex] = React.useState<number | null>(null)
  const [groupFilter, setGroupFilter] = React.useState<string>('all')
  const [pricingByType, setPricingByType] = React.useState<Record<PricingType, Record<string, PricingResponse>>>({
    plans: {},
    credits: {},
  })
  const [supportedCurrenciesByType, setSupportedCurrenciesByType] = React.useState<Record<PricingType, string[]>>({
    plans: [],
    credits: [],
  })
  const [pricingLoading, setPricingLoading] = React.useState(false)
  const [pricingError, setPricingError] = React.useState<string | null>(null)
  const [pricingDebug, setPricingDebug] = React.useState<PricingDebugEntry[]>([])
  const [pricingDebugExpanded, setPricingDebugExpanded] = React.useState(false)
  const [pricingReloadToken, setPricingReloadToken] = React.useState(0)

  const loadData = React.useCallback(async () => {
    setLoading(true)
    setNotice(null)

    try {
      const settingsRes = await getSuperadminSubscriptionSettings()
      setProducts(settingsRes.products ?? [])
      setProductGroups(settingsRes.product_groups ?? [])
      setPricingCacheTtlHours(settingsRes.pricing_cache_ttl_hours ?? 6)
      setPricingLastUpdated(settingsRes.pricing_last_updated ?? null)
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to load product settings.'
      setNotice({ type: 'error', message })
    } finally {
      setLoading(false)
    }
  }, [])

  React.useEffect(() => {
    void loadData()
  }, [loadData])

  const groupOptions = productGroups.length ? productGroups : fallbackGroups

  const filteredItems = React.useMemo(
    () =>
      products
        .map((product, index) => ({ product, index }))
        .filter((item) => groupFilter === 'all' || item.product.group === groupFilter),
    [products, groupFilter],
  )

  React.useEffect(() => {
    if (products.length === 0) {
      setSelectedIndex(null)
      return
    }

    if (filteredItems.length === 0) {
      setSelectedIndex(null)
      return
    }

    const isSelectedVisible =
      selectedIndex !== null && filteredItems.some((item) => item.index === selectedIndex)
    if (!isSelectedVisible) {
      setSelectedIndex(filteredItems[0].index)
    }
  }, [products.length, filteredItems, selectedIndex])

  const handleSaveProducts = async () => {
    setSaving(true)
    setNotice(null)

    try {
      const response = await saveSuperadminSubscriptionSettings({
        products,
        pricing_cache_ttl_hours: pricingCacheTtlHours,
      })
      setProducts(response.products ?? products)
      setProductGroups(response.product_groups ?? productGroups)
      setPricingCacheTtlHours(response.pricing_cache_ttl_hours ?? pricingCacheTtlHours)
      setPricingLastUpdated(response.pricing_last_updated ?? pricingLastUpdated)
      setNotice({ type: 'success', message: 'Product settings saved.' })
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to save product settings.'
      setNotice({ type: 'error', message })
    } finally {
      setSaving(false)
    }
  }

  const handleRefreshPricing = async () => {
    setSaving(true)
    setNotice(null)

    try {
      const response = await saveSuperadminSubscriptionSettings({
        refresh_pricing: true,
      })
      setPricingLastUpdated(response.pricing_last_updated ?? pricingLastUpdated)
      setNotice({ type: 'success', message: 'Pricing cache refreshed.' })
      setPricingReloadToken((prev) => prev + 1)
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to refresh pricing cache.'
      setNotice({ type: 'error', message })
    } finally {
      setSaving(false)
    }
  }

  const handleProductChange = (
    index: number,
    field: keyof ProductDefinition,
    value: string | boolean | string[],
  ) => {
    setProducts((prev) =>
      prev.map((product, idx) => {
        if (idx !== index) {
          return product
        }
        return { ...product, [field]: value } as ProductDefinition
      }),
    )
  }

  const handleProductBulletsChange = (index: number, value: string) => {
    const bullets = value
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => line.length > 0)
    handleProductChange(index, 'bullets', bullets)
  }

  const handleAddProduct = () => {
    const fallbackGroup = (productGroups[0] ?? 'individual') as ProductDefinition['group']
    const nextGroup =
      groupFilter === 'all' ? fallbackGroup : (groupFilter as ProductDefinition['group'])
    setProducts((prev) => {
      const next = [
        ...prev,
        {
          key: '',
          group: nextGroup,
          label: '',
          description: '',
          bullets: [],
          active: true,
        },
      ]
      setSelectedIndex(next.length - 1)
      return next
    })
  }

  const handleRemoveProduct = (index: number) => {
    setProducts((prev) => {
      const next = prev.filter((_product, idx) => idx !== index)
      setSelectedIndex((current) => {
        if (next.length === 0) {
          return null
        }
        if (current === null) {
          return 0
        }
        if (current === index) {
          return index > 0 ? index - 1 : 0
        }
        if (current > index) {
          return current - 1
        }
        return current
      })
      return next
    })
  }

  const selectedProduct = selectedIndex !== null ? products[selectedIndex] : null
  const pricingType: PricingType | null = selectedProduct
    ? selectedProduct.group === 'credits'
      ? 'credits'
      : 'plans'
    : null

  const formatAmount = React.useCallback((price: PricingPrice) => {
    if (price.unit_amount === null) {
      return 'Custom'
    }

    const amount = price.unit_amount / 100
    const currency = price.currency || 'USD'

    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(amount)
    } catch {
      return `${currency} ${amount.toFixed(2)}`
    }
  }, [])

  const formatInterval = React.useCallback((price: PricingPrice) => {
    if (!price.interval) {
      return 'One-off'
    }

    const count = price.interval_count ?? 1
    const plural = count === 1 ? '' : 's'
    return `Every ${count} ${price.interval}${plural}`
  }, [])

  const formatLocalDateTime = React.useCallback((value: string | null) => {
    if (!value) {
      return 'Not refreshed yet'
    }

    const parsed = new Date(value.replace(' ', 'T') + 'Z')

    if (Number.isNaN(parsed.getTime())) {
      return value
    }

    return parsed.toLocaleString()
  }, [])

  const pricingEntries = React.useMemo(() => {
    if (!selectedProduct || !pricingType) {
      return []
    }

    const supportedCurrencies = supportedCurrenciesByType[pricingType] ?? []
    const responses = pricingByType[pricingType] ?? {}

    return supportedCurrencies.map((currency) => {
      const response = responses[currency]
      const productEntry = response?.products.find((item) => item.key === selectedProduct.key)
      return {
        currency,
        response,
        productEntry,
        price: productEntry?.price ?? null,
      }
    })
  }, [pricingByType, pricingType, selectedProduct, supportedCurrenciesByType])

  const renderCurrencyLabel = React.useCallback((currency: string, priceId?: string) => {
    const flag = currencyFlags[currency] ?? ''
    const label = `${flag ? `${flag} ` : ''}${currency}`
    const tooltip = priceId ? `Price ID: ${priceId}` : 'No Stripe price configured.'

    return (
      <Tooltip title={tooltip} placement="top">
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="subtitle2">{label}</Typography>
        </Box>
      </Tooltip>
    )
  }, [])

  React.useEffect(() => {
    if (!selectedProduct || !selectedProduct.key || !pricingType) {
      setPricingError(null)
      return
    }

    let cancelled = false

    const loadPricing = async () => {
      setPricingLoading(true)
      setPricingError(null)
      setPricingDebug([])

      const buildPath = (type: PricingType, currency?: string, force?: boolean) => {
        const search = new URLSearchParams()
        search.set('type', type)
        if (currency) {
          search.set('currency', currency)
        }
        if (force) {
          search.set('force', '1')
        }
        return `billing/pricing?${search.toString()}`
      }

      const recordDebug = (entry: PricingDebugEntry) => {
        setPricingDebug((prev) => [...prev, entry])
      }

      const fetchPricing = async (type: PricingType, currency?: string, force?: boolean) => {
        const path = buildPath(type, currency, force)
        const requestEntry: PricingDebugEntry = {
          id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
          requestedAt: new Date().toISOString(),
          request: {
            type,
            currency,
            force,
            path: `${path}&debug=1`,
          },
          status: 'success',
        }

        try {
          const response = await getPricing(type, currency, { force, debug: true })
          recordDebug({ ...requestEntry, response, status: 'success' })
          return response
        } catch (error) {
          const err = error as Error & { status?: number; data?: unknown }
          recordDebug({
            ...requestEntry,
            status: 'error',
            error: {
              message: err.message || 'Request failed.',
              status: err.status,
              data: err.data,
            },
          })
          throw error
        }
      }

      try {
        const baseResponse = await fetchPricing(pricingType)
        if (cancelled) {
          return
        }

        const supported = baseResponse.supported_currencies ?? []
        const responsesByCurrency: Record<string, PricingResponse> = {
          [baseResponse.currency]: baseResponse,
        }

        const missingCurrencies = supported.filter((currency) => currency !== baseResponse.currency)
        if (missingCurrencies.length > 0) {
          const extraResponses = await Promise.all(
            missingCurrencies.map((currency) => fetchPricing(pricingType, currency)),
          )
          extraResponses.forEach((response) => {
            responsesByCurrency[response.currency] = response
          })
        }

        const missingPriceCurrencies = supported.filter((currency) => {
          const response = responsesByCurrency[currency]
          const entry = response?.products.find((item) => item.key === selectedProduct.key)
          return !entry?.price
        })

        if (missingPriceCurrencies.length > 0) {
          const forcedResponses = await Promise.all(
            missingPriceCurrencies.map((currency) => fetchPricing(pricingType, currency, true)),
          )
          forcedResponses.forEach((response) => {
            responsesByCurrency[response.currency] = response
          })
        }

        if (!cancelled) {
          setPricingByType((prev) => ({
            ...prev,
            [pricingType]: {
              ...prev[pricingType],
              ...responsesByCurrency,
            },
          }))
          setSupportedCurrenciesByType((prev) => ({
            ...prev,
            [pricingType]: supported,
          }))
        }
      } catch (error) {
        if (!cancelled) {
          const message = error instanceof Error ? error.message : 'Unable to load pricing data.'
          setPricingError(message)
        }
      } finally {
        if (!cancelled) {
          setPricingLoading(false)
        }
      }
    }

    void loadPricing()

    return () => {
      cancelled = true
    }
  }, [pricingReloadToken, pricingType, selectedProduct?.key])

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Stack spacing={3}>
      <Stack
        direction={{ xs: 'column', lg: 'row' }}
        spacing={2}
        alignItems={{ xs: 'flex-start', lg: 'center' }}
        justifyContent="space-between"
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="h4" component="h1">
            Products
          </Typography>
          <Tooltip
            title={
              <>
                <Typography variant="body2" component="p">
                  Manage the RPA product catalog used on pricing pages. Stripe pricing and trials stay in Stripe, but
                  the metadata here drives the UI.
                </Typography>
                <Typography variant="body2" component="p" sx={{ mt: 1 }}>
                  Each Stripe product should include metadata key <strong>rpa_product_key</strong> with the matching key
                  below.
                </Typography>
              </>
            }
            placement="bottom-start"
          >
            <IconButton aria-label="Product manager help" size="small">
              <InfoOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, width: { xs: '100%', lg: 'auto' } }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            alignItems={{ xs: 'stretch', md: 'center' }}
          >
            <TextField
              label="Pricing cache TTL (hours)"
              value={pricingCacheTtlHours}
              onChange={(event) => setPricingCacheTtlHours(Number(event.target.value))}
              type="number"
              inputProps={{ min: 1, max: 168 }}
              sx={{ maxWidth: 220 }}
              size="small"
            />
            <Typography color="text.secondary">
              Last updated: {formatLocalDateTime(pricingLastUpdated)}
            </Typography>
            <Button variant="outlined" onClick={handleRefreshPricing} disabled={saving}>
              {saving ? 'Refreshing...' : 'Refresh pricing'}
            </Button>
          </Stack>
        </Paper>
      </Stack>

      {notice ? <Alert severity={notice.type}>{notice.message}</Alert> : null}

      <Paper variant="outlined" sx={{ borderRadius: 2 }}>
        <Stack direction={{ xs: 'column', lg: 'row' }} divider={<Divider flexItem orientation="vertical" />}>
          <Stack sx={{ p: 3, width: { xs: '100%', lg: 320 }, flexShrink: 0 }} spacing={2}>
            <Stack spacing={1.5}>
              <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                <Typography variant="h6">Products</Typography>
                <Button variant="outlined" size="small" onClick={handleAddProduct} disabled={saving}>
                  Add product
                </Button>
              </Stack>
              <FormControl size="small">
                <FormLabel>Group</FormLabel>
                <Select value={groupFilter} onChange={(event) => setGroupFilter(event.target.value)}>
                  <MenuItem value="all">All</MenuItem>
                  {groupOptions.map((group) => (
                    <MenuItem key={group} value={group}>
                      {group}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>

            {loading ? (
              <Typography color="text.secondary">Loading products...</Typography>
            ) : filteredItems.length === 0 ? (
              <Typography color="text.secondary">No products configured yet.</Typography>
            ) : (
              <List disablePadding dense>
                {filteredItems.map(({ product, index }) => {
                  const title = product.label || 'Untitled product'
                  return (
                    <ListItemButton
                      key={`${product.key}-${index}`}
                      selected={selectedIndex === index}
                      onClick={() => setSelectedIndex(index)}
                      sx={{ borderRadius: 2, mb: 0.25, py: 0.75 }}
                    >
                      <ListItemText primary={title} />
                    </ListItemButton>
                  )
                })}
              </List>
            )}
          </Stack>

          <Stack sx={{ p: 3, flex: 1, minWidth: 0 }} spacing={2}>
            {selectedProduct ? (
              <Stack spacing={2}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }}>
                  <TextField
                    label="Title"
                    value={selectedProduct.label}
                    onChange={(event) => handleProductChange(selectedIndex ?? 0, 'label', event.target.value)}
                    InputProps={{
                      sx: { fontSize: '1.5rem', fontWeight: 600 },
                    }}
                    sx={{ flex: 1 }}
                  />
                  <Button variant="contained" onClick={handleSaveProducts} disabled={saving}>
                    {saving ? 'Saving...' : 'Save changes'}
                  </Button>
                </Stack>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }}>
                  <TextField
                    label="Product key"
                    value={selectedProduct.key}
                    onChange={(event) => handleProductChange(selectedIndex ?? 0, 'key', event.target.value)}
                    helperText="Matches Stripe metadata (rpa_product_key)."
                    size="small"
                  />
                  <FormControl size="small" sx={{ minWidth: 160 }}>
                    <FormLabel>Group</FormLabel>
                    <Select
                      value={selectedProduct.group}
                      onChange={(event) => handleProductChange(selectedIndex ?? 0, 'group', event.target.value)}
                    >
                      {groupOptions.map((group) => (
                        <MenuItem key={group} value={group}>
                          {group}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={selectedProduct.active}
                        onChange={(event) =>
                          handleProductChange(selectedIndex ?? 0, 'active', event.target.checked)
                        }
                      />
                    }
                    label={selectedProduct.active ? 'Active' : 'Hidden'}
                  />
                  <Button color="error" onClick={() => handleRemoveProduct(selectedIndex ?? 0)}>
                    Remove
                  </Button>
                </Stack>
                <TextField
                  label="Description"
                  value={selectedProduct.description}
                  onChange={(event) => handleProductChange(selectedIndex ?? 0, 'description', event.target.value)}
                  size="small"
                  multiline
                  minRows={2}
                />
                <TextField
                  label="Bullet points (one per line)"
                  value={selectedProduct.bullets.join('\n')}
                  onChange={(event) => handleProductBulletsChange(selectedIndex ?? 0, event.target.value)}
                  size="small"
                  multiline
                  minRows={3}
                />
                <Divider />
                <Stack spacing={1.5}>
                  <Typography variant="subtitle1">Stripe pricing</Typography>
                  {pricingLoading ? (
                    <Typography color="text.secondary">Loading pricing...</Typography>
                  ) : null}
                  {pricingError ? <Alert severity="error">{pricingError}</Alert> : null}
                  {!pricingLoading && !pricingError && pricingEntries.length === 0 ? (
                    <Typography color="text.secondary">No pricing data available yet.</Typography>
                  ) : null}
                  {!pricingLoading && !pricingError && pricingEntries.length > 0 ? (
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', md: 'repeat(auto-fit, minmax(220px, 1fr))' },
                        gap: 1.5,
                      }}
                    >
                      {pricingEntries.map((entry) => (
                        <Paper key={entry.currency} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                          <Stack spacing={1}>
                            {renderCurrencyLabel(entry.currency, entry.price?.id)}
                            {entry.price ? (
                              <Stack spacing={0.5}>
                                <Typography variant="body2">Amount: {formatAmount(entry.price)}</Typography>
                                <Typography variant="body2">Interval: {formatInterval(entry.price)}</Typography>
                                <Typography variant="body2">
                                  Trial days: {entry.price.trial_days ?? 'None'}
                                </Typography>
                                {entry.response?.last_updated ? (
                                  <Typography variant="body2" color="text.secondary">
                                    Last updated: {formatLocalDateTime(entry.response.last_updated)}
                                  </Typography>
                                ) : null}
                              </Stack>
                            ) : (
                              <Typography color="text.secondary">
                                No Stripe price configured for {entry.currency}.
                              </Typography>
                            )}
                          </Stack>
                        </Paper>
                      ))}
                    </Box>
                  ) : null}
                  <Divider />
                  <Accordion
                    expanded={pricingDebugExpanded}
                    onChange={(_event, expanded) => setPricingDebugExpanded(expanded)}
                    variant="outlined"
                    sx={{ borderRadius: 2 }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="pricing-debug-content">
                      <Typography variant="subtitle2">Pricing debug</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      {pricingDebug.length === 0 ? (
                        <Typography color="text.secondary">No pricing requests logged yet.</Typography>
                      ) : (
                        <Stack spacing={1.5}>
                          {pricingDebug.map((entry) => (
                            <Paper key={entry.id} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                              <Stack spacing={1}>
                                {(() => {
                                  const responseDebug = (
                                    entry.response as unknown as
                                      | { debug?: { cache?: { source?: string; cache_key?: string } } }
                                      | undefined
                                  )?.debug
                                  const errorDebug = (
                                    entry.error?.data as
                                      | { debug?: { cache?: { source?: string; cache_key?: string } } }
                                      | undefined
                                  )?.debug
                                  const cacheSource = responseDebug?.cache?.source ?? errorDebug?.cache?.source
                                  const cacheKey = responseDebug?.cache?.cache_key ?? errorDebug?.cache?.cache_key

                                  if (!cacheSource) {
                                    return null
                                  }

                                  return (
                                    <Typography variant="body2">
                                      Source: {cacheSource}
                                      {cacheKey ? ` (${cacheKey})` : ''}
                                    </Typography>
                                  )
                                })()}
                                <Typography variant="body2">
                                  {entry.requestedAt} - {entry.request.path}
                                </Typography>
                                <Typography variant="body2">
                                  Status: {entry.status === 'success' ? 'OK' : 'Error'}
                                </Typography>
                                {entry.error ? (
                                  <Typography variant="body2" color="error">
                                    {entry.error.message}
                                  </Typography>
                                ) : null}
                                <Box
                                  component="pre"
                                  sx={{
                                    whiteSpace: 'pre-wrap',
                                    fontFamily: 'monospace',
                                    fontSize: 12,
                                    margin: 0,
                                    color: 'text.secondary',
                                  }}
                                >
                                  {JSON.stringify(entry.response ?? entry.error?.data ?? null, null, 2)}
                                </Box>
                              </Stack>
                            </Paper>
                          ))}
                        </Stack>
                      )}
                    </AccordionDetails>
                  </Accordion>
                </Stack>
              </Stack>
            ) : (
              <Typography color="text.secondary">Select a product to view details.</Typography>
            )}
          </Stack>
        </Stack>
      </Paper>
      </Stack>
    </Box>
  )
}

export default ProductManagerPage
